import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Trash2, Plus, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BiExclusions({ open, onClose, onChanged, note }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [cands, setCands] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/bi/exclusions`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { if (open) { load(); setQ(""); setCands([]); } }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) { setCands([]); return; }
    const t = setTimeout(() => axios.get(`${API}/admin/bi/exclusions/candidates`, { params: { q } }).then((r) => setCands(r.data)).catch(() => setCands([])), 250);
    return () => clearTimeout(t);
  }, [q]);

  const add = async (c) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/bi/exclusions`, { name: c.name, customer_id: c.customer_id || null });
      toast.success(`${c.name} escluso dalle statistiche`);
      setQ(""); setCands([]);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); } finally { setBusy(false); }
  };
  const remove = async (x) => {
    try { await axios.delete(`${API}/admin/bi/exclusions/${x.id}`); toast.success(`${x.name} torna nelle statistiche`); await load(); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const excludedNorm = new Set(list.map((x) => x.name.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="bi-exclusions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#8A1C2A]" /> Clienti esclusi dalle statistiche</DialogTitle>
          <p className="text-xs text-stone-500">{note}</p>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente da escludere (min 2 lettere)..." className="h-12 w-full pl-9 pr-3 rounded-xl border border-stone-300 text-sm" data-testid="bi-exclusions-search" />
          </div>
          {cands.length > 0 && (
            <div className="border border-stone-200 rounded-xl divide-y divide-stone-100 max-h-60 overflow-y-auto" data-testid="bi-exclusions-candidates">
              {cands.map((c) => {
                const already = excludedNorm.has(c.name.toLowerCase());
                return (
                  <button key={`${c.customer_id || ""}|${c.name}`} type="button" disabled={already || busy} onClick={() => add(c)} className="w-full text-left flex items-center gap-3 px-3 py-2.5 min-h-[48px] hover:bg-stone-50 disabled:opacity-50" data-testid={`bi-exclusion-candidate-${c.name}`}>
                    <div className="flex-1 min-w-0"><p className="font-medium text-stone-900 truncate">{c.name}</p><p className="text-[11px] text-stone-500">{c.origin}{c.customer_id ? " · in anagrafica" : ""}</p></div>
                    {already ? <span className="text-[11px] text-stone-400">già escluso</span> : <Plus className="w-4 h-4 text-[#8A1C2A]" />}
                  </button>
                );
              })}
            </div>
          )}
          {q.trim().length >= 2 && !cands.length && <p className="text-xs text-stone-500 italic">Nessun cliente trovato</p>}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">Esclusi ({list.length})</p>
            {!list.length && <p className="text-sm text-stone-500 italic" data-testid="bi-exclusions-empty">Nessun cliente escluso: tutte le vendite contano nelle statistiche.</p>}
            <div className="divide-y divide-stone-100 border border-stone-200 rounded-xl overflow-hidden">
              {list.map((x) => (
                <div key={x.id} className="flex items-center gap-3 px-3 py-2.5 min-h-[52px]" data-testid={`bi-exclusion-row-${x.id}`}>
                  <div className="flex-1 min-w-0"><p className="font-medium text-stone-900 truncate">{x.name}</p><p className="text-[11px] text-stone-500">{x.customer_id ? "abbinato all'anagrafica" : "per ragione sociale"} · {x.created_by}</p></div>
                  <button type="button" onClick={() => remove(x)} className="w-11 h-11 rounded-xl flex items-center justify-center text-red-700 hover:bg-red-50" aria-label="Rimuovi esclusione" data-testid={`bi-exclusion-remove-${x.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
